/**
 * Page Timeline — Sticky Behavior
 *
 * The timeline lives in the header group, but CSS position:sticky can't
 * work because <body> is a flex container. Instead, this script uses an
 * IntersectionObserver sentinel + position:fixed with a continuous rAF
 * loop that keeps the top offset in sync with the visible header height,
 * even when the header state changes without a scroll event.
 *
 * - When announcement + header show: timeline fixed below header
 * - When only header shows: timeline fixed below header (higher)
 * - When neither shows: timeline fixed at top of page
 */
(function () {
  "use strict";

  var timelines = document.querySelectorAll(".page-timeline");
  if (!timelines.length) return;

  var rafId = null;
  var resizeTimer;
  var stuckCount = 0;

  /**
   * Returns the visible height of an element. An element is considered
   * invisible if it has been scrolled completely above the viewport
   * (its bottom edge is <= 0) or is display:none / visibility:hidden.
   */
  function getVisibleHeight(el) {
    if (!el) return 0;
    var style = getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden") return 0;
    var rect = el.getBoundingClientRect();
    if (rect.height <= 0) return 0;
    // Visible portion = how much of the element is still within the viewport.
    // Smoothly decreases from rect.height to 0 as the element scrolls above
    // the viewport, avoiding the abrupt jump that causes visual flashes.
    return Math.max(0, Math.min(rect.height, rect.bottom));
  }

  /**
   * Calculates the visible offset (announcement + header heights)
   * and applies it to all currently-fixed timelines.
   */
  function updateOffset() {
    var announcement = document.querySelector(".announcement-bar");
    var header = document.querySelector("header-component");

    var offset = 0;
    if (announcement) offset += getVisibleHeight(announcement);
    if (header) offset += getVisibleHeight(header);

    document.body.style.setProperty("--timeline-top-offset", offset + "px");

    for (var i = 0; i < timelines.length; i++) {
      if (timelines[i].dataset.stuck === "true") {
        timelines[i].style.top = offset + "px";
      }
    }
  }

  /**
   * Continuous rAF loop — runs while any timeline is stuck to keep
   * the top offset in sync with header state changes that happen
   * without scroll events (e.g. delayed hide animations).
   */
  function startLoop() {
    if (rafId) return;
    function loop() {
      updateOffset();
      if (stuckCount > 0) {
        rafId = requestAnimationFrame(loop);
      } else {
        rafId = null;
      }
    }
    rafId = requestAnimationFrame(loop);
  }

  /**
   * Sets a timeline to fixed (stuck) position.
   */
  function stickTimeline(timeline) {
    if (timeline.dataset.stuck === "true") return;

    // Recalculate offset NOW — on page load updateOffset() may have run
    // before the header was in the DOM (split-hero Phase 1 hides it),
    // leaving --timeline-top-offset at 0px until the first scroll.
    updateOffset();
    var offset = getComputedStyle(document.body).getPropertyValue("--timeline-top-offset").trim() || "0px";

    // Reserve EXACT current space BEFORE removing from flow.
    // Using wrapper.offsetHeight (not timeline.offsetHeight) captures
    // the full wrapper height including any padding/borders/other children.
    var wrapper = timeline.parentNode;
    if (wrapper) wrapper.style.minHeight = wrapper.offsetHeight + "px";

    timeline.style.position = "fixed";
    timeline.style.top = offset;
    timeline.style.left = "0";
    timeline.style.right = "0";
    timeline.style.zIndex = "3";
    timeline.dataset.stuck = "true";
    stuckCount++;

    startLoop();
  }

  /**
   * Returns a timeline to normal flow.
   */
  function unstickTimeline(timeline) {
    if (timeline.dataset.stuck !== "true") return;

    // Clear space reservation BEFORE restoring to flow to prevent double-height
    var wrapper = timeline.parentNode;
    if (wrapper) wrapper.style.minHeight = "";

    timeline.style.position = "";
    timeline.style.top = "";
    timeline.style.left = "";
    timeline.style.right = "";
    timeline.style.zIndex = "";
    timeline.dataset.stuck = "";
    stuckCount = Math.max(0, stuckCount - 1);
  }

  /**
   * Sets up a sentinel + IntersectionObserver for one timeline.
   */
  function setupTimeline(timeline) {
    var wrapper = timeline.parentNode;
    if (!wrapper) return;

    var sentinel = document.createElement("div");
    sentinel.className = "page-timeline__sentinel";
    sentinel.setAttribute("aria-hidden", "true");
    sentinel.style.cssText = "height:0;width:0;pointer-events:none;";
    wrapper.insertBefore(sentinel, timeline);

    var observer = new IntersectionObserver(
      function (entries) {
        // Skip hidden wrappers (left-side timeline hidden by split-hero CSS)
        if (getComputedStyle(wrapper).display === "none") return;
        if (entries[0].isIntersecting) {
          unstickTimeline(timeline);
        } else {
          stickTimeline(timeline);
        }
      },
      {threshold: 0},
    );

    observer.observe(sentinel);
  }

  /**
   * Moves all .page-timeline-section wrappers from inside #header-group
   * to between #header-group and <main>. This prevents Dwell's header
   * height calculations from being affected by minHeight changes on
   * the wrappers during stick/unstick transitions.
   */
  function relocateTimelines() {
    var headerGroup = document.getElementById("header-group");
    if (!headerGroup) return;

    var sections = document.querySelectorAll(".page-timeline-section");
    for (var i = 0; i < sections.length; i++) {
      var section = sections[i];
      if (section.parentNode === headerGroup) {
        headerGroup.parentNode.insertBefore(section, headerGroup.nextSibling);
      }
    }
  }

  // Initialize
  relocateTimelines();
  updateOffset();

  for (var i = 0; i < timelines.length; i++) {
    setupTimeline(timelines[i]);
  }

  // Recalculate on resize
  window.addEventListener("resize", function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(updateOffset, 150);
  });

  // =========================================================================
  // Phase 3 — Per-Stage Range Tracking
  // =========================================================================
  //
  // Each stage has its own active range, defined by two points:
  //
  //   Scroll mode:     data-scroll-start / data-scroll-end (percentages 0–100)
  //   Section mode:    data-section-start / data-section-end (section IDs)
  //
  // In scroll mode, a stage is active when scrollPercent falls within its range.
  // In section mode, a stage is active when its start section is visible and
  //   its end section has not yet entered the viewport.
  //
  // Click-to-scroll works in both modes (reads data-scroll-target).

  // ---------------------------------------------------------------------------
  // Shared helpers
  // ---------------------------------------------------------------------------

  function resolveSectionElement(sectionId) {
    if (!sectionId) return null;
    var trimmed = sectionId.trim();
    if (!trimmed) return null;
    return document.querySelector('[id$="__' + trimmed + '"]');
  }

  function setActiveStage(timeline, activeIndex) {
    var stages = timeline.querySelectorAll(".page-timeline__stage");
    for (var i = 0; i < stages.length; i++) {
      stages[i].setAttribute("data-active", i === activeIndex ? "true" : "false");
    }
  }

  /**
   * Returns the index of the first stage without a data-url attribute.
   * Returns -1 if all stages are URL-only.
   */
  function findFirstTrackableStage(timeline) {
    var stages = timeline.querySelectorAll(".page-timeline__stage");
    for (var i = 0; i < stages.length; i++) {
      if (!stages[i].hasAttribute("data-url")) return i;
    }
    return -1;
  }

  // ---------------------------------------------------------------------------
  // Mode: scroll (per-stage percentage ranges)
  // ---------------------------------------------------------------------------

  var scrollRafId = null;

  function getScrollPercent() {
    var docHeight = document.documentElement.scrollHeight;
    var winHeight = window.innerHeight;
    var scrollable = docHeight - winHeight;
    if (scrollable <= 0) return 0;
    return Math.max(0, Math.min(1, window.pageYOffset / scrollable));
  }

  /**
   * Finds the active stage based on per-stage scroll ranges.
   * A stage is active when scrollPercent ∈ [scrollStart, scrollEnd).
   * First matching stage wins. Defaults to first stage.
   */
  function findActiveByScroll(timeline) {
    var stages = timeline.querySelectorAll(".page-timeline__stage");
    var numStages = stages.length;
    if (numStages === 0) return -1;

    // Count trackable stages (those without data-url)
    var trackableCount = 0;
    for (var j = 0; j < numStages; j++) {
      if (!stages[j].hasAttribute("data-url")) trackableCount++;
    }
    if (trackableCount === 0) return -1;
    if (trackableCount === 1) return findFirstTrackableStage(timeline);

    var scrollPercent = getScrollPercent();

    for (var i = 0; i < numStages; i++) {
      // Skip URL-only stages — they don't participate in scroll tracking
      if (stages[i].hasAttribute("data-url")) continue;

      var start = parseFloat(stages[i].getAttribute("data-scroll-start")) || 0;
      var end = parseFloat(stages[i].getAttribute("data-scroll-end"));
      if (isNaN(end)) end = 100;

      if (scrollPercent >= start / 100 && scrollPercent < end / 100) {
        return i;
      }
    }

    return findFirstTrackableStage(timeline);
  }

  var scrollModeTimelines = [];

  function onScroll() {
    if (scrollRafId) return;
    scrollRafId = requestAnimationFrame(function () {
      scrollRafId = null;
      for (var i = 0; i < scrollModeTimelines.length; i++) {
        if (getComputedStyle(scrollModeTimelines[i]).display === "none") continue;
        var idx = findActiveByScroll(scrollModeTimelines[i]);
        if (idx >= 0) setActiveStage(scrollModeTimelines[i], idx);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Mode: section (per-stage start/end sections via IntersectionObserver)
  // ---------------------------------------------------------------------------

  /**
   * Builds two maps for a timeline:
   *   startMap: sectionElement → stageIndex (for data-section-start)
   *   endMap:   sectionElement → stageIndex (for data-section-end)
   * Returns {startMap, endMap, hasAny: bool}.
   */
  function buildSectionRangeMaps(timeline) {
    var stages = timeline.querySelectorAll(".page-timeline__stage");
    var startMap = new Map();
    var endMap = new Map();
    var hasAny = false;

    for (var i = 0; i < stages.length; i++) {
      // Skip URL-only stages — they don't participate in section tracking
      if (stages[i].hasAttribute("data-url")) continue;

      var startId = (stages[i].getAttribute("data-section-start") || "").trim();
      var endId = (stages[i].getAttribute("data-section-end") || "").trim();

      if (startId) {
        var startEl = resolveSectionElement(startId);
        if (startEl) {
          startMap.set(startEl, i);
          hasAny = true;
        }
      }
      if (endId) {
        var endEl = resolveSectionElement(endId);
        if (endEl) {
          endMap.set(endEl, i);
          hasAny = true;
        }
      }
    }

    return {startMap: startMap, endMap: endMap, hasAny: hasAny};
  }

  function setupSectionTracking(timeline) {
    var stages = timeline.querySelectorAll(".page-timeline__stage");
    var numStages = stages.length;

    var firstTrackable = findFirstTrackableStage(timeline);

    if (numStages <= 1 || firstTrackable < 0) {
      if (firstTrackable >= 0) setActiveStage(timeline, firstTrackable);
      return false;
    }

    var maps = buildSectionRangeMaps(timeline);
    if (!maps.hasAny) {
      if (firstTrackable >= 0) setActiveStage(timeline, firstTrackable);
      return false;
    }

    // Track intersection ratios per stage: startRatios[stageIndex], endRatios[stageIndex]
    var startRatios = {};
    var endRatios = {};

    var lastActiveStage = 0;

    function updateActive() {
      var bestStage = -1;
      var bestScore = -Infinity;

      for (var i = 0; i < numStages; i++) {
        var s = startRatios[i] || 0;
        var e = endRatios[i] || 0;
        var score = s - e;
        if (score > bestScore) {
          bestScore = score;
          bestStage = i;
        }
      }

      // Only switch when a stage has a positive score and it differs from current.
      // This prevents flickering to stage 0 in gaps between sections.
      if (bestScore > 0 && bestStage !== lastActiveStage) {
        lastActiveStage = bestStage;
        setActiveStage(timeline, bestStage);
      }
    }

    var observer = new IntersectionObserver(
      function (entries) {
        var changed = false;
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          var target = entry.target;
          var ratio = getComputedStyle(target).display === "none" ? 0 : entry.intersectionRatio;

          if (maps.startMap.has(target)) {
            startRatios[maps.startMap.get(target)] = ratio;
            changed = true;
          }
          if (maps.endMap.has(target)) {
            endRatios[maps.endMap.get(target)] = ratio;
            changed = true;
          }
        }
        if (changed) updateActive();
      },
      {
        rootMargin: "0px 0px -" + (parseInt(timeline.getAttribute("data-viewport-threshold"), 10) || 50) + "% 0px",
        threshold: [0, 0.1, 0.25, 0.5, 0.75],
      },
    );

    maps.startMap.forEach(function (stageIndex, el) {
      observer.observe(el);
    });
    maps.endMap.forEach(function (stageIndex, el) {
      observer.observe(el);
    });

    timeline._sectionObserver = observer;
    if (firstTrackable >= 0) setActiveStage(timeline, firstTrackable);
    return true;
  }

  // ---------------------------------------------------------------------------
  // Click-to-scroll (both modes)
  // ---------------------------------------------------------------------------

  function scrollToStage(stage) {
    var targetId = (stage.getAttribute("data-scroll-target") || "").trim();
    if (!targetId) {
      // Fall back to section_start_id
      targetId = (stage.getAttribute("data-section-start") || "").trim();
    }
    if (!targetId) return;

    var targetEl = resolveSectionElement(targetId);
    if (!targetEl) return;

    var timeline = stage.closest(".page-timeline");
    var timelineHeight = timeline ? timeline.offsetHeight : 0;

    // Determine if scrolling up (target is above the current viewport).
    var scrollingUp = targetEl.getBoundingClientRect().top < 0;

    // When scrolling up and the header has a sticky mode that reveals on
    // scroll-up, the header will reappear and must be offset so the target
    // doesn't end up hidden behind it.
    var stickyHeaderHeight = 0;
    if (scrollingUp) {
      var header = document.querySelector("#header-component");
      if (header) {
        var stickyMode = header.getAttribute("sticky") || "";
        if (stickyMode === "scroll-up" || stickyMode === "always") {
          stickyHeaderHeight = header.offsetHeight;
        }
      }
    }

    // Current visible offset (announcement + visible header portion).
    var currentOffset = parseFloat(getComputedStyle(document.body).getPropertyValue("--timeline-top-offset").trim()) || 0;

    // Use the larger of the current offset and the sticky header height
    // to ensure the target is fully visible after the header reappears.
    var headerOffset = Math.max(currentOffset, stickyHeaderHeight);
    var totalOffset = headerOffset + timelineHeight;
    var targetTop = targetEl.getBoundingClientRect().top + window.pageYOffset - totalOffset;

    window.scrollTo({top: targetTop, behavior: "smooth"});
  }

  function setupClickHandlers(timeline) {
    timeline.addEventListener("click", function (event) {
      var stage = event.target.closest(".page-timeline__stage");
      if (!stage) return;

      // URL mode — navigate to the linked page (same tab)
      var url = (stage.getAttribute("data-url") || "").trim();
      if (url) {
        window.location.href = url;
        return;
      }

      // Immediately activate the clicked stage so the icon updates
      // without waiting for the IntersectionObserver or scroll event.
      var stages = timeline.querySelectorAll(".page-timeline__stage");
      for (var i = 0; i < stages.length; i++) {
        if (stages[i] === stage) {
          setActiveStage(timeline, i);
          break;
        }
      }

      scrollToStage(stage);
    });
  }

  // ---------------------------------------------------------------------------
  // Initialization
  // ---------------------------------------------------------------------------

  for (var t = 0; t < timelines.length; t++) {
    var timeline = timelines[t];
    setupClickHandlers(timeline);

    var mode = timeline.getAttribute("data-tracking-mode") || "section";

    if (mode === "scroll") {
      var idx = findActiveByScroll(timeline);
      if (idx >= 0) setActiveStage(timeline, idx);
      scrollModeTimelines.push(timeline);
    } else {
      var observerCreated = setupSectionTracking(timeline);
      if (!observerCreated) {
        var fallbackIdx = findActiveByScroll(timeline);
        if (fallbackIdx >= 0) setActiveStage(timeline, fallbackIdx);
        scrollModeTimelines.push(timeline);
      }
    }
  }

  if (scrollModeTimelines.length > 0) {
    window.addEventListener("scroll", onScroll, {passive: true});
  }
})();
